import { ReactNode, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import logExploreMapDistanceAwaySliderClicked from '@alltrails/amplitude/events/logExploreMapDistanceAwaySliderClicked';
import { kilometersToMeters, metersToKilometers, metersToMiles, milesToMeters } from '@alltrails/data-formatting/distanceConverters';
import { SliderFilterConfig } from '@alltrails/denali/components/FilterBar';
import { LocationResult } from '../../redux/exploreServiceSlice';

export const distanceAwayFilterId = 'distance-away';

const kmValues = Array.from(Array(12).keys()).map(v => v * 10); // 0-110, in 10 km increments, 110 acts as "Any" (100km is the max mapbox handles)

const miValues = Array.from(Array(8).keys()).map(v => v * 10); // 0-70, in 10 mile increments. 70 acts as "Any"

export const getValidDistanceAway = (meters: number, useMetric: boolean) => {
  const values = useMetric ? kmValues : miValues;

  const value = useMetric ? metersToKilometers(meters) : metersToMiles(meters);
  const validValue = values.find(v => value <= v);
  if (validValue === undefined) {
    return undefined;
  }

  return useMetric ? kilometersToMeters(validValue) : milesToMeters(validValue);
};

// When we show the user an apply button, we don't show the "any" option so that they can apply the filter
// by just hitting apply, they don't actually have to adjust the value if they don't want to.
export const getDefaultDistanceAwayMeters = (useMetric: boolean) => {
  const values = useMetric ? kmValues : miValues;
  const value = values[values.length - 2];
  return useMetric ? kilometersToMeters(value) : milesToMeters(value);
};

type Args = {
  appliedDistanceAway?: number | undefined; // The value can be applied either onChange or onApply, so this can differ from distanceAway
  distanceAway: number | undefined;
  hideAnyOption?: boolean;
  locationResult?: LocationResult;
  onApplyClick?: () => void;
  onClear?: () => void;
  onDistanceAwayChange: (updatedDistance: number | undefined) => void;
  onDistanceAwayChangeEnd?: (updatedDistance: number | undefined) => void;
  subHeader?: ReactNode;
  useMetric: boolean;
};

const useDistanceAwayFilterConfig = ({
  appliedDistanceAway,
  distanceAway,
  hideAnyOption,
  locationResult,
  onApplyClick,
  onClear,
  onDistanceAwayChange,
  onDistanceAwayChangeEnd,
  subHeader,
  useMetric
}: Args): SliderFilterConfig => {
  const intl = useIntl();

  const unit = useMetric ? 'kilometer' : 'mile';
  const unitValues = useMetric ? kmValues : miValues;
  const anyValue = unitValues[unitValues.length - 1];
  const values = hideAnyOption ? unitValues.slice(0, -1) : unitValues;

  const toMeters = useCallback(
    (distance: number) => {
      return useMetric ? kilometersToMeters(distance) : milesToMeters(distance);
    },
    [useMetric]
  );

  const fromMeters = useCallback(
    (distance: number) => {
      return Math.round(useMetric ? metersToKilometers(distance) : metersToMiles(distance));
    },
    [useMetric]
  );

  const marks = useMemo(
    () =>
      values.map((value, index) => {
        let label: string | undefined = undefined;
        if (index === 0) {
          label = intl.formatNumber(value, { style: 'unit', unit });
        } else if (index === values.length - 1) {
          label = value === anyValue ? intl.formatMessage({ defaultMessage: 'Any' }) : intl.formatNumber(value, { style: 'unit', unit });
        }
        return { value, label };
      }),
    [anyValue, intl, unit, values]
  );

  const onChange = useCallback(
    (value: number) => {
      logExploreMapDistanceAwaySliderClicked({
        action_taken: 'slider clicked',
        from_search_location: Boolean(locationResult),
        searched_location: locationResult?.name ?? ''
      });
      if (value === anyValue) {
        onDistanceAwayChange(undefined);
      } else {
        // Don't let users select 0
        const newDistance = value === values[0] ? values[1] : value;
        onDistanceAwayChange(toMeters(newDistance));
      }
    },
    [anyValue, locationResult, onDistanceAwayChange, toMeters, values]
  );

  const onChangeEnd = useMemo(() => {
    if (onDistanceAwayChangeEnd) {
      return (value: number) => {
        if (value === anyValue) {
          onDistanceAwayChangeEnd(undefined);
        } else {
          // Don't let users select 0
          const newDistance = value === values[0] ? values[1] : value;
          onDistanceAwayChangeEnd(toMeters(newDistance));
        }
      };
    }
    return undefined;
  }, [anyValue, onDistanceAwayChangeEnd, toMeters, values]);

  const apply = useMemo(() => {
    if (onApplyClick) {
      return () => {
        logExploreMapDistanceAwaySliderClicked({
          action_taken: 'apply cta',
          from_search_location: Boolean(locationResult),
          searched_location: locationResult?.name ?? ''
        });
        onApplyClick();
      };
    }
    return undefined;
  }, [locationResult, onApplyClick]);

  const clear = useMemo(() => {
    if (onClear) {
      return () => {
        logExploreMapDistanceAwaySliderClicked({
          action_taken: 'reset cta',
          from_search_location: Boolean(locationResult),
          searched_location: locationResult?.name ?? ''
        });
        onClear();
      };
    }
    return undefined;
  }, [locationResult, onClear]);

  return useMemo(
    () => ({
      type: 'slider',
      id: distanceAwayFilterId,
      filterChip: {
        onClear: clear,
        selectedItems: appliedDistanceAway
          ? [
              intl.formatMessage(
                { defaultMessage: 'Within {distance}' },
                { distance: intl.formatNumber(fromMeters(appliedDistanceAway), { style: 'unit', unit }) }
              )
            ]
          : undefined,
        testId: 'distance-away-filter-chip',
        unselectedText: intl.formatMessage({ defaultMessage: 'Distance away' })
      },
      purchaseTier: 'plus',
      onApplyClick: apply,
      slider: {
        formatHeaderValue: value => {
          if (value === anyValue) {
            return intl.formatMessage({ defaultMessage: 'Any' });
          }
          return intl.formatNumber(value, { style: 'unit', unit });
        },
        label: intl.formatMessage({ defaultMessage: 'Distance away' }),
        marks,
        subHeader,
        onChange,
        onChangeEnd,
        testId: 'distance-away-filter',
        value: distanceAway ? fromMeters(distanceAway) : anyValue
      }
    }),
    [clear, appliedDistanceAway, intl, fromMeters, unit, apply, marks, subHeader, onChange, onChangeEnd, distanceAway, anyValue]
  );
};

export default useDistanceAwayFilterConfig;
